import { supabase } from '../lib/supabase';
import cacheService from './cacheService';
import { env } from '../config/environment';

/**
 * Converts a relative Supabase Storage path to a full public URL.
 * Some older profiles have avatar_url stored as "/objects/uploads/..." instead of
 * the full https://...supabase.co/storage/v1/object/public/... URL.
 */
function resolveAvatarUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = (env.supabaseUrl || 'https://kyxfbmqiamspfacaiqjg.supabase.co').replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}/storage/v1/object/public${path}`;
}

/**
 * Profile Photo Cache Service
 * Handles caching of user profile photos to avoid repeated loading
 */
export interface CachedProfilePhoto {
  userId: string;
  avatar_url: string | null;
  cachedAt: number;
}

class ProfilePhotoCacheService {
  private readonly CACHE_KEY_PREFIX = 'profile_photo:';

  /**
   * Get cached profile photo URL for a user
   * @param userId - The user ID to get the photo for
   * @param forceRefresh - If true, bypasses cache and fetches directly from database
   */
  async getProfilePhoto(userId: string, forceRefresh: boolean = false): Promise<string | null> {
    if (!userId) return null;

    const key = `${this.CACHE_KEY_PREFIX}${userId}`;
    
    try {
      // If forceRefresh, invalidate the existing cache first
      if (forceRefresh) {
        console.log(`🔄 Force refreshing profile photo for user: ${userId.substring(0, 8)}...`);
        await cacheService.invalidate(key);
      }

      const cachedPhoto = await cacheService.get(
        key,
        async () => {
          console.log(`📸 Fetching profile photo for user: ${userId.substring(0, 8)}...`);
          
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('avatar_url')
            .eq('id', userId)
            .maybeSingle();

          if (error) {
            console.error('❌ Error fetching profile photo:', error);
            throw new Error(`Failed to fetch profile photo: ${error.message}`);
          }

          if (!profile) {
            console.log(`ℹ️ No profile found for user: ${userId.substring(0, 8)}...`);
          }

          const photoData: CachedProfilePhoto = {
            userId,
            avatar_url: resolveAvatarUrl(profile?.avatar_url),
            cachedAt: Date.now()
          };

          console.log('✅ Profile photo fetched successfully:', {
            userId: userId.substring(0, 8) + '...',
            hasPhoto: !!photoData.avatar_url
          });

          return photoData;
        },
        cacheService.getConfig('PROFILE_PHOTOS')
      );

      // If cache returned null, try one more time with a fresh fetch
      // This handles the case where a stale null was cached before a user uploaded a photo
      if (!cachedPhoto?.avatar_url && !forceRefresh) {
        console.log(`🔍 Cache returned null for user ${userId.substring(0, 8)}, checking for fresh data...`);
        return this.getProfilePhoto(userId, true);
      }

      return resolveAvatarUrl(cachedPhoto?.avatar_url) || null;
    } catch (error) {
      console.error(`❌ ProfilePhotoCacheService: Error getting photo for user ${userId.substring(0, 8)}...:`, error);
      return null;
    }
  }

  /**
   * Update cached profile photo URL when user uploads a new photo
   */
  async updateProfilePhoto(userId: string, avatarUrl: string | null): Promise<void> {
    if (!userId) return;

    const key = `${this.CACHE_KEY_PREFIX}${userId}`;

    try {
      console.log(`🔄 Updating cached profile photo for user: ${userId.substring(0, 8)}...`);

      const photoData: CachedProfilePhoto = {
        userId,
        avatar_url: avatarUrl,
        cachedAt: Date.now()
      };

      // Update cache directly without fetching from database
      await cacheService.set(key, photoData, cacheService.getConfig('PROFILE_PHOTOS'));

      console.log('✅ Profile photo cache updated successfully:', {
        userId: userId.substring(0, 8) + '...',
        hasPhoto: !!avatarUrl
      });
    } catch (error) {
      console.error(`❌ ProfilePhotoCacheService: Error updating photo cache for user ${userId.substring(0, 8)}...:`, error);
    }
  }

  /**
   * Invalidate profile photo cache for a specific user
   */
  async invalidateUserPhoto(userId: string): Promise<void> {
    if (!userId) return;

    const key = `${this.CACHE_KEY_PREFIX}${userId}`;

    try {
      console.log(`🗑️ Invalidating profile photo cache for user: ${userId.substring(0, 8)}...`);
      await cacheService.invalidate(key);
      console.log('✅ Profile photo cache invalidated');
    } catch (error) {
      console.error(`❌ ProfilePhotoCacheService: Error invalidating photo cache for user ${userId.substring(0, 8)}...:`, error);
    }
  }

  /**
   * Clear all profile photo caches
   */
  async clearAllPhotoCaches(): Promise<void> {
    try {
      console.log('🗑️ Clearing all profile photo caches...');
      await cacheService.invalidatePattern(this.CACHE_KEY_PREFIX);
      console.log('✅ All profile photo caches cleared');
    } catch (error) {
      console.error('❌ ProfilePhotoCacheService: Error clearing all photo caches:', error);
    }
  }

  /**
   * Preload profile photo for better performance
   */
  async preloadProfilePhoto(userId: string): Promise<void> {
    if (!userId) return;

    try {
      console.log(`🚀 Preloading profile photo for user: ${userId.substring(0, 8)}...`);
      
      const photoUrl = await this.getProfilePhoto(userId);
      
      if (photoUrl) {
        console.log(`✅ Preloaded profile photo: ${userId.substring(0, 8)}...`);
      } else {
        console.log(`ℹ️ No profile photo to preload: ${userId.substring(0, 8)}...`);
      }
    } catch (error) {
      console.error('❌ Error preloading profile photo:', error);
    }
  }

  /**
   * Get multiple profile photos in batch (for lists/feeds)
   */
  async getBatchProfilePhotos(userIds: string[]): Promise<Record<string, string | null>> {
    const result: Record<string, string | null> = {};

    if (!userIds.length) return result;

    console.log(`📸 Batch loading ${userIds.length} profile photos...`);

    // Load all photos in parallel for better performance
    const photoPromises = userIds.map(async (userId) => {
      const photoUrl = await this.getProfilePhoto(userId);
      return { userId, photoUrl };
    });

    const photos = await Promise.all(photoPromises);

    // Build result object
    photos.forEach(({ userId, photoUrl }) => {
      result[userId] = photoUrl;
    });

    console.log(`✅ Batch loaded ${userIds.length} profile photos`);
    return result;
  }
}

// Export singleton instance
export const profilePhotoCacheService = new ProfilePhotoCacheService();
export default profilePhotoCacheService;
export { resolveAvatarUrl };